/**
 * SAH onboarding readiness - the computed gate (NOT a stored flag) that decides
 * whether a participant is ready for funded service delivery + claiming.
 *
 * This is the SSOT for "is this SAH client ready", used to surface the
 * onboarding checklist and to derive the narrower financial-posting gate. Each
 * check is `required` (blocks readiness) or advisory.
 */

import { SAH_DELIVERY_STATUS_LABELS } from './sah-delivery-ladder';

export type SahReadinessCheckKey =
  | 'classification'
  | 'means_tier'
  | 'care_consent'
  | 'budget'
  | 'hcp_opening'
  | 'consents'
  | 'care_plan'
  | 'service_agreement'
  | 'head_provider'
  | 'signatory';

export type SahCarePlanDeliveryStatus =
  | 'MISSING'
  | 'SUSPENDED'
  | 'DRAFT_ONLY'
  | 'AWAITING_ACKNOWLEDGEMENT'
  | 'SERVICE_INCLUSIONS_MISSING'
  | 'SERVICE_AGREEMENT_MISSING'
  | 'SERVICE_AGREEMENT_STALE'
  | 'CARE_CONSENT_MISSING'
  | 'BUDGET_MISSING'
  | 'HCP_OPENING_MISSING'
  | 'DELIVERY_READY';

export type SahReadinessCheck = {
  key: SahReadinessCheckKey;
  label: string;
  required: boolean;
  satisfied: boolean;
  detail?: string;
};

export type SahReadinessInput = {
  /** A standard SAH class, transitioned-HCP level, or governed pathway basis is recorded. */
  hasClassification: boolean;
  /** A means-test tier is recorded. */
  hasMeansTier: boolean;
  /** A date-current ongoing-quarter budget or governed pathway envelope is provisioned. */
  hasCurrentBudget: boolean;
  /** The participant is a grandfathered HCP transitioner. */
  isTransitioner: boolean;
  /** The HCP legacy-unspent opening has been recorded (transitioners only). */
  hasHcpOpening: boolean;
  /** Deprecated compatibility input: SAH consent gaps are advisory, not blockers. */
  missingRequiredConsents: readonly string[];
  /** Generic care-services consent is recorded. This is a delivery-readiness gate. */
  hasCareConsent: boolean;
  /** A SAH care plan exists. Deprecated for readiness: pass carePlanDeliveryStatus. */
  hasCarePlan: boolean;
  /** Canonical SAH care-plan delivery contract state. */
  carePlanDeliveryStatus?: SahCarePlanDeliveryStatus;
  /** A signed service agreement exists. */
  hasSignedAgreement: boolean;
  /** The derived signatory is BLOCKED (no authorised signer). */
  signatoryBlocked: boolean;
  /**
   * The funded stream is settled brokered_out (delivered under a registered head
   * provider). Default false. When true, a head provider MUST be linked — the
   * associated-provider lawfulness control (Aged Care Act 2024 s 11(6)): the
   * tenant has no own registration to deliver this funded client under.
   */
  isBrokeredOut?: boolean;
  /** A head registered provider is linked to the funded stream. Default false. */
  hasHeadProvider?: boolean;
  /**
   * The client is delivered by an associated provider under the linked head
   * provider. The head provider owns agreement, contribution, budget and core
   * consent evidence; their absence must never make care unschedulable here.
   */
  heldByHeadProvider?: boolean;
  /** Human-readable holder name for the operator-facing readiness row. */
  headProviderName?: string | null;
};

export type SahReadiness = {
  checks: SahReadinessCheck[];
  /** All REQUIRED checks satisfied. */
  ready: boolean;
  /** The required checks that are not yet satisfied. */
  blockers: SahReadinessCheck[];
  /** Canonical SAH care-plan delivery contract state. */
  carePlanDeliveryStatus: SahCarePlanDeliveryStatus;
};

export function deriveSahCarePlanDeliveryStatus(input: {
  hasCarePlan: boolean;
  isSuspended?: boolean;
  hasActiveAgreedCarePlan: boolean;
  hasAcknowledgedCarePlan: boolean;
  /** Opt-in. Live SQL only blocks when the plan stamped the template true. */
  acknowledgementRequired?: boolean;
  hasTypedServiceInclusions: boolean;
  hasSignedAgreement: boolean;
  hasCareConsent: boolean;
  hasCurrentBudget: boolean;
  isHcpTransitioner?: boolean;
  hasHcpOpening?: boolean;
}): SahCarePlanDeliveryStatus {
  if (!input.hasCarePlan) return 'MISSING';
  if (input.isSuspended) return 'SUSPENDED';
  if (!input.hasActiveAgreedCarePlan) return 'DRAFT_ONLY';
  if (!input.hasAcknowledgedCarePlan && input.acknowledgementRequired === true) {
    return 'AWAITING_ACKNOWLEDGEMENT';
  }
  if (!input.hasTypedServiceInclusions) return 'SERVICE_INCLUSIONS_MISSING';
  if (!input.hasSignedAgreement) return 'SERVICE_AGREEMENT_MISSING';
  if (!input.hasCareConsent) return 'CARE_CONSENT_MISSING';
  if (!input.hasCurrentBudget) return 'BUDGET_MISSING';
  if ((input.isHcpTransitioner ?? false) && !(input.hasHcpOpening ?? false)) {
    return 'HCP_OPENING_MISSING';
  }
  return 'DELIVERY_READY';
}

const SAH_CARE_PLAN_DELIVERY_STATUS_VALUES: readonly SahCarePlanDeliveryStatus[] = [
  'MISSING',
  'SUSPENDED',
  'DRAFT_ONLY',
  'AWAITING_ACKNOWLEDGEMENT',
  'SERVICE_INCLUSIONS_MISSING',
  'SERVICE_AGREEMENT_MISSING',
  'SERVICE_AGREEMENT_STALE',
  'CARE_CONSENT_MISSING',
  'BUDGET_MISSING',
  'HCP_OPENING_MISSING',
  'DELIVERY_READY',
];

export function isSahCarePlanDeliveryStatus(value: string): value is SahCarePlanDeliveryStatus {
  return (SAH_CARE_PLAN_DELIVERY_STATUS_VALUES as readonly string[]).includes(value);
}

/** Map the authoritative G-C delivery_status into computeSahReadiness input. */
export function carePlanDeliveryStatusFromProjection(
  deliveryStatus: string | null | undefined,
  options: { hasCarePlan: boolean },
): SahCarePlanDeliveryStatus {
  if (!options.hasCarePlan) return 'MISSING';
  if (deliveryStatus && isSahCarePlanDeliveryStatus(deliveryStatus)) return deliveryStatus;
  return 'DRAFT_ONLY';
}

export function isSahHcpOpeningRecorded(input: {
  hcp_transition_opening_recorded?: boolean | null;
  hcp_legacy_carry_forward?: number | null;
}): boolean {
  const carryForward = Number(input.hcp_legacy_carry_forward ?? 0);
  const finiteCarryForward = Number.isFinite(carryForward) ? carryForward : 0;
  return (input.hcp_transition_opening_recorded ?? false) || finiteCarryForward > 0;
}

export function sahCarePlanStatusLabel(status: SahCarePlanDeliveryStatus): string {
  switch (status) {
    case 'MISSING':
      return SAH_DELIVERY_STATUS_LABELS.MISSING;
    case 'SUSPENDED':
      return SAH_DELIVERY_STATUS_LABELS.SUSPENDED;
    case 'DRAFT_ONLY':
      return SAH_DELIVERY_STATUS_LABELS.DRAFT_ONLY;
    case 'AWAITING_ACKNOWLEDGEMENT':
      return SAH_DELIVERY_STATUS_LABELS.AWAITING_ACKNOWLEDGEMENT;
    case 'SERVICE_INCLUSIONS_MISSING':
      return SAH_DELIVERY_STATUS_LABELS.SERVICE_INCLUSIONS_MISSING;
    case 'SERVICE_AGREEMENT_MISSING':
      return SAH_DELIVERY_STATUS_LABELS.SERVICE_AGREEMENT_MISSING;
    case 'SERVICE_AGREEMENT_STALE':
      return SAH_DELIVERY_STATUS_LABELS.SERVICE_AGREEMENT_STALE;
    case 'CARE_CONSENT_MISSING':
      return SAH_DELIVERY_STATUS_LABELS.CARE_CONSENT_MISSING;
    case 'BUDGET_MISSING':
      return SAH_DELIVERY_STATUS_LABELS.BUDGET_MISSING;
    case 'HCP_OPENING_MISSING':
      return SAH_DELIVERY_STATUS_LABELS.HCP_OPENING_MISSING;
    case 'DELIVERY_READY':
      return SAH_DELIVERY_STATUS_LABELS.DELIVERY_READY;
  }
}

function nonBlankString(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

export function isCompleteSahServiceInclusion(
  input: {
    service_code?: unknown;
    service_category?: unknown;
    unit_type?: unknown;
    frequency_per_period?: unknown;
    unit_cost?: unknown;
    start_date?: unknown;
    end_date?: unknown;
  },
  asOfDate?: string | null,
): boolean {
  const frequency = Number(input.frequency_per_period);
  const unitCost = Number(input.unit_cost);
  const startDate = typeof input.start_date === 'string' ? input.start_date : null;
  const endDate = typeof input.end_date === 'string' ? input.end_date : null;
  const asOf = typeof asOfDate === 'string' && asOfDate.length > 0 ? asOfDate : null;

  return (
    nonBlankString(input.service_code) &&
    nonBlankString(input.service_category) &&
    nonBlankString(input.unit_type) &&
    input.frequency_per_period != null &&
    Number.isFinite(frequency) &&
    frequency > 0 &&
    input.unit_cost != null &&
    Number.isFinite(unitCost) &&
    unitCost >= 0 &&
    nonBlankString(input.start_date) &&
    (!asOf || !startDate || startDate <= asOf) &&
    (!asOf || !endDate || endDate >= asOf)
  );
}

/** Input must already be scoped to the participant/programme by the controlled reader. */
export function isCurrentSahParticipantBudget(
  input: {
    period_start?: unknown;
    period_end?: unknown;
    allocated_amount?: unknown;
  },
  asOfDate: string,
): boolean {
  if (typeof input.period_start !== 'string' || typeof input.period_end !== 'string') return false;
  if (!(input.period_start <= asOfDate && asOfDate <= input.period_end)) return false;
  const allocatedAmount = Number(input.allocated_amount);
  return input.allocated_amount != null && Number.isFinite(allocatedAmount) && allocatedAmount > 0;
}

export function isCurrentSahPlanBudget(
  input: {
    care_plan_id?: unknown;
    period_start?: unknown;
    period_end?: unknown;
    allocated_amount?: unknown;
  },
  carePlanId: string | null | undefined,
  asOfDate: string,
): boolean {
  return (
    Boolean(carePlanId) &&
    input.care_plan_id === carePlanId &&
    isCurrentSahParticipantBudget(input, asOfDate)
  );
}

function carePlanCheckSatisfied(status: SahCarePlanDeliveryStatus): boolean {
  return (
    status === 'DELIVERY_READY' ||
    status === 'SERVICE_AGREEMENT_MISSING' ||
    status === 'BUDGET_MISSING'
  );
}

export function computeSahReadiness(input: SahReadinessInput): SahReadiness {
  const carePlanDeliveryStatus =
    input.carePlanDeliveryStatus ?? (input.hasCarePlan ? 'DRAFT_ONLY' : 'MISSING');
  const heldDetail = input.heldByHeadProvider
    ? `Held by head provider${input.headProviderName ? ` (${input.headProviderName})` : ''}`
    : undefined;
  const checks: SahReadinessCheck[] = [
    {
      key: 'classification',
      label: 'Classification or governed funding basis recorded',
      required: true,
      satisfied: input.hasClassification,
    },
    {
      key: 'means_tier',
      label: 'Means-test tier recorded',
      required: true,
      satisfied: input.heldByHeadProvider || input.hasMeansTier,
      detail: heldDetail,
    },
    {
      key: 'care_plan',
      label: 'SAH funding and claims ready',
      required: true,
      satisfied: input.heldByHeadProvider || carePlanCheckSatisfied(carePlanDeliveryStatus),
      detail:
        heldDetail ??
        (carePlanDeliveryStatus === 'DELIVERY_READY'
          ? undefined
          : sahCarePlanStatusLabel(carePlanDeliveryStatus)),
    },
    {
      key: 'care_consent',
      label: 'General care consent recorded',
      required: true,
      satisfied: input.heldByHeadProvider || input.hasCareConsent,
      detail: heldDetail,
    },
    {
      key: 'budget',
      label: 'Current funding budget provisioned',
      required: true,
      satisfied: input.heldByHeadProvider || input.hasCurrentBudget,
      detail: heldDetail,
    },
    {
      key: 'hcp_opening',
      label: 'HCP legacy-unspent opening recorded',
      // Only required for transitioners.
      required: input.isTransitioner,
      satisfied: !input.isTransitioner || input.hasHcpOpening,
      detail: input.isTransitioner ? undefined : 'Not a transitioner',
    },
    {
      key: 'consents',
      label: 'Recommended SAH consents reviewed',
      required: false,
      satisfied: input.missingRequiredConsents.length === 0,
      detail:
        input.missingRequiredConsents.length > 0
          ? `Outstanding recommendations: ${input.missingRequiredConsents.join(', ')}`
          : undefined,
    },
    {
      key: 'signatory',
      label: 'An authorised agreement signatory exists',
      required: true,
      satisfied: input.heldByHeadProvider || !input.signatoryBlocked,
      detail: heldDetail,
    },
    {
      key: 'service_agreement',
      label: 'Service agreement signed',
      required: true,
      satisfied: input.heldByHeadProvider || input.hasSignedAgreement,
      detail: heldDetail,
    },
    {
      key: 'head_provider',
      label: 'Head registered provider linked (brokered)',
      // Required only when the funded stream is brokered_out: an associated
      // provider must record the registered head provider it delivers under,
      // otherwise it is delivering funded care with no registration above it.
      required: input.isBrokeredOut ?? false,
      satisfied: !(input.isBrokeredOut ?? false) || (input.hasHeadProvider ?? false),
      detail: (input.isBrokeredOut ?? false) ? undefined : 'Not a brokered stream',
    },
  ];

  const blockers = checks.filter((c) => c.required && !c.satisfied);
  return { checks, ready: blockers.length === 0, blockers, carePlanDeliveryStatus };
}

/**
 * The subset of readiness required before FINANCIAL POSTING (drawdown / claim)
 * may proceed. Money posting must hard-fail if the budget is missing. SAH
 * consents are recommended or condition-specific, not an unconditional posting
 * block; keep the legacy input only so older callers compile unchanged.
 */
export function canPostSahFinancials(input: {
  hasCurrentBudget: boolean;
  missingRequiredConsents: readonly string[];
}): { ok: boolean; reason?: string } {
  if (!input.hasCurrentBudget) {
    return { ok: false, reason: 'No current-quarter SAH budget is provisioned for this client.' };
  }
  return { ok: true };
}
